// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: an HTML page or Markdown document shown in place (design/html-and-subagents h1, h3, md1,
// md3, h6, md6): its title and file name with Code / Copy / Download, the page as a still picture
// or the document in Konvo's type with a fade, and what it is with Open. It loads once it nears
// the screen; the source supplies the loader (chat attachment, agent file, attachment on a reply).
// DocumentRow is the same file as one line, for places where the file is a reference, not the point.
// A page made for the chat runs in place with no card around it, where that is safe (LivePage).

import {DocumentKindIcon, documentTitle} from '@app/features/document_preview/DocumentKind';
import type {MarkdownPictureLoader} from '@app/features/document_preview/DocumentPicture';
import styles from '@app/features/document_preview/DocumentPreview.module.css';
import {
	DOCUMENT_PREVIEW_MAX_BYTES,
	type DocumentKind,
	markdownTasks,
} from '@app/features/document_preview/DocumentPreviewUtils';
import type {DocumentMode} from '@app/features/document_preview/DocumentView';
import type {HtmlAssetLoader} from '@app/features/document_preview/HtmlAssets';
import {HtmlFrame} from '@app/features/document_preview/HtmlFrame';
import {useWebPicturesSource, type WebPictures} from '@app/features/document_preview/HtmlWebPictures';
import {pagesCanRunInPlace} from '@app/features/document_preview/LivePage';
import {MarkdownDocument} from '@app/features/document_preview/MarkdownDocument';
import {useNearViewport} from '@app/features/messaging/hooks/useNearViewport';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import {htmlIsT3Page} from '@app/features/messaging/utils/HtmlPreviewDocument';
import {Spinner} from '@app/features/ui/components/Spinner';
import {plural} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconArrowsMaximize, IconChevronRight, IconCode, IconCopy, IconDownload, IconRefresh} from '@tabler/icons-react';
import {clsx} from 'clsx';
import type React from 'react';
import {useEffect, useState} from 'react';

/** Loaded texts by key, so a card that scrolls back into view doesn't fetch again. */
const loaded = new Map<string, string>();
const LOADED_LIMIT = 30;
/** The preview height of a card's page or document. */
const PREVIEW_HEIGHT = 220;
/** A page made for the chat shows whole up to this height; taller ones fade out, with Open for the rest. */
const PAGE_PREVIEW_MAX_HEIGHT = 900;
/** The padding Konvo adds round a T3 page, which the height it asked for doesn't know about. */
const T3_PAGE_ROOM = 32;
/** A chat page that didn't say its height. */
const PAGE_PREVIEW_HEIGHT = 480;

function remember(key: string, text: string) {
	loaded.delete(key);
	loaded.set(key, text);
	while (loaded.size > LOADED_LIMIT) loaded.delete(loaded.keys().next().value as string);
}

export class DocumentTooLargeError extends Error {}
/** Thrown by a loader when the file isn't there (any more): the card then stays out of the way. */
export class DocumentMissingError extends Error {}

export interface DocumentCardProps {
	kind: DocumentKind;
	name: string;
	/** Bytes, when known before loading. */
	size?: number;
	/** A stable key for this file (its URL, or machine/thread/path). */
	cacheKey: string;
	load: (signal: AbortSignal) => Promise<string>;
	/** Where it came from, shown in the viewer, e.g. "Markus Mulder · 11:20". */
	subtitle?: string;
	onDownload?: () => void;
	/** Loads the pictures, stylesheets and fonts an HTML page names beside it. */
	loadAsset?: HtmlAssetLoader;
	/** Gives an HTML page's pictures from the web through the media proxy. */
	webPictures?: WebPictures;
	/** Gives an address for each picture a Markdown document shows (beside it, or on the web). */
	loadPicture?: MarkdownPictureLoader;
	/**
	 * An HTML page made to be shown in the chat (T3's html_render): its preview lays out at the card's
	 * width, at the height the page asked for, whole up to PAGE_PREVIEW_MAX_HEIGHT.
	 */
	page?: {height?: number};
	/** Shown instead of the card while it can't load (e.g. a hidden spoiler). */
	disabled?: boolean;
	/** Called once the loader says the file isn't there (the card then shows nothing). */
	onMissing?: () => void;
	className?: string;
	flx?: string;
}

type DocumentSource = Pick<
	DocumentCardProps,
	| 'kind'
	| 'name'
	| 'size'
	| 'cacheKey'
	| 'load'
	| 'subtitle'
	| 'onDownload'
	| 'loadAsset'
	| 'webPictures'
	| 'loadPicture'
	| 'disabled'
	| 'onMissing'
>;

/** What a card and a row share: the text, loaded once it nears the screen, what it is, and the viewer. */
function useDocument({
	kind,
	name,
	size,
	cacheKey,
	load,
	subtitle,
	onDownload,
	loadAsset,
	webPictures,
	loadPicture,
	disabled = false,
	onMissing,
}: DocumentSource) {
	const {t} = useLingui();
	// The viewer opens outside this tree, so it gets the inherited source from here.
	const pictures = useWebPicturesSource(webPictures);
	const {ref, isNearViewport} = useNearViewport<HTMLDivElement>({rememberKey: cacheKey});
	const [text, setText] = useState<string | null>(() => loaded.get(cacheKey) ?? null);
	const [error, setError] = useState<'large' | 'failed' | 'missing' | null>(
		size !== undefined && size > DOCUMENT_PREVIEW_MAX_BYTES ? 'large' : null,
	);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (disabled || !isNearViewport || text !== null || error === 'large' || error === 'missing') return;
		const controller = new AbortController();
		setError(null);
		load(controller.signal)
			.then((value) => {
				if (controller.signal.aborted) return;
				remember(cacheKey, value);
				setText(value);
			})
			.catch((reason) => {
				if (controller.signal.aborted) return;
				setError(
					reason instanceof DocumentTooLargeError
						? 'large'
						: reason instanceof DocumentMissingError
							? 'missing'
							: 'failed',
				);
			});
		return () => controller.abort();
	}, [disabled, isNearViewport, cacheKey, attempt]);
	useEffect(() => {
		if (error === 'missing') onMissing?.();
	}, [error]);

	const open = (mode: DocumentMode = 'view') => {
		if (text === null) return;
		// The viewer loads on demand, so a card stays light wherever it's used.
		void import('@app/features/document_preview/DocumentViewer').then(({openDocumentViewer}) =>
			openDocumentViewer({
				kind,
				name,
				text,
				subtitle,
				initialMode: mode,
				onDownload,
				assets: loadAsset,
				webPictures: pictures,
				pictures: loadPicture,
			}),
		);
	};
	const tasks = kind === 'markdown' && text !== null ? markdownTasks(text) : {done: 0, total: 0};
	const {done: doneCount, total: totalCount} = tasks;
	const bytes = size ?? (text !== null ? new Blob([text]).size : undefined);
	return {
		ref,
		pictures,
		text,
		error,
		retry: () => setAttempt(attempt + 1),
		open,
		title: text !== null ? documentTitle(kind, text, name) : name,
		what:
			kind === 'html'
				? t({message: 'HTML page', comment: 'Document card: what the file is.'})
				: t({message: 'Markdown', comment: 'Document card: what the file is.'}),
		sizeText: bytes !== undefined ? formatFileSize(bytes) : null,
		tasksText: tasks.total
			? plural(doneCount, {one: `# of ${totalCount} done`, other: `# of ${totalCount} done`})
			: null,
		note:
			error === 'large'
				? t({
						message: 'Too large to show here. Download it to open it.',
						comment: 'Document card: the file is bigger than Konvo previews.',
					})
				: error === 'failed'
					? t({message: "Couldn't load this file.", comment: 'Document card: loading the file failed.'})
					: null,
		tryAgain: t({message: 'Try again', comment: 'Document card: load the file again.'}),
	};
}

export type DocumentRowProps = Omit<DocumentSource, 'disabled'> & {className?: string; flx?: string};

/**
 * A document as one line, drawn like the changes row under a reply: its title, what it is, Download,
 * and the whole row opens the viewer (which has the code and the copy). Several go in a `rows` group.
 */
export function DocumentRow({className, flx = 'document-preview.row', ...source}: DocumentRowProps) {
	const {t} = useLingui();
	const {kind, name, subtitle, onDownload} = source;
	const {ref, text, error, retry, open, title, what, sizeText, tasksText, note, tryAgain} = useDocument(source);
	// A file the reply named that isn't there: its name stays in the reply as text.
	if (error === 'missing') return null;
	const facts = [title !== name ? name : what, sizeText, tasksText].filter(Boolean).join(' · ');
	return (
		<div ref={ref} className={clsx(styles.row, className)} data-flx={flx} data-kind={kind}>
			<span className={styles.rowIcon}>
				<DocumentKindIcon kind={kind} size={18} />
			</span>
			<button
				type="button"
				className={styles.rowOpen}
				onClick={() => open()}
				disabled={text === null}
				aria-label={t({message: `Open ${title}`, comment: 'Document card: open the file in the viewer.'})}
				title={subtitle ?? name}
				data-flx={`${flx}.open`}
			>
				<span className={styles.rowTitle}>{title}</span>
				<span className={styles.rowFacts} data-tone={note ? 'note' : undefined}>
					{note ?? facts}
				</span>
			</button>
			{error === 'failed' && (
				<button
					type="button"
					className={styles.rowTool}
					onClick={retry}
					aria-label={tryAgain}
					title={tryAgain}
					data-flx={`${flx}.retry`}
				>
					<IconRefresh size={17} stroke={1.8} />
				</button>
			)}
			{onDownload && (
				<button
					type="button"
					className={styles.rowTool}
					onClick={onDownload}
					aria-label={t({message: 'Download', comment: 'Document viewer: download the file.'})}
					title={t({message: 'Download', comment: 'Document viewer: download the file.'})}
					data-flx={`${flx}.download`}
				>
					<IconDownload size={17} stroke={1.7} />
				</button>
			)}
			{text === null && !error ? (
				<span className={styles.rowEnd}>
					<Spinner size="small" />
				</span>
			) : (
				text !== null && <IconChevronRight size={16} stroke={1.8} className={styles.rowEnd} aria-hidden="true" />
			)}
		</div>
	);
}

export function DocumentCard({page, className, flx = 'document-preview.card', ...source}: DocumentCardProps) {
	const {i18n, t} = useLingui();
	const {kind, name, onDownload, loadAsset, loadPicture, disabled = false} = source;
	const {ref, pictures, text, error, retry, open, title, what, sizeText, tasksText, note, tryAgain} =
		useDocument(source);
	const asked = page ? (page.height ?? PAGE_PREVIEW_HEIGHT) : undefined;
	const pageRoom = text !== null && htmlIsT3Page(text) ? T3_PAGE_ROOM : 0;
	const pageHeight = asked === undefined ? undefined : Math.min(asked + pageRoom, PAGE_PREVIEW_MAX_HEIGHT);
	const pageCropped = asked !== undefined && asked > PAGE_PREVIEW_MAX_HEIGHT;
	const facts = [what, sizeText, tasksText].filter(Boolean).join(' · ');
	const [live] = useState(() => !!page && kind === 'html' && pagesCanRunInPlace());
	// The page's own height once it has said; until then what the agent asked for, to hold the place.
	const [liveHeight, setLiveHeight] = useState<number | null>(null);

	// Until the text is here: loading, or why it isn't.
	const waiting: React.ReactNode =
		error === 'large' ? (
			<p className={styles.cardNote}>{note}</p>
		) : error === 'failed' ? (
			<p className={styles.cardNote}>
				{note}
				<button type="button" className={styles.inlineButton} onClick={retry} data-flx={`${flx}.retry`}>
					<IconRefresh size={14} stroke={1.8} />
					{tryAgain}
				</button>
			</p>
		) : (
			<div className={styles.cardLoading}>
				<Spinner size="small" />
			</div>
		);
	let body: React.ReactNode = waiting;
	if (!error && text !== null) {
		body = (
			<button
				type="button"
				className={styles.cardPreview}
				onClick={() => open()}
				aria-label={t({message: `Open ${title}`, comment: 'Document card: open the file in the viewer.'})}
				data-flx={`${flx}.preview`}
			>
				{kind === 'html' ? (
					<HtmlFrame
						html={text}
						title={title}
						thumbnail={true}
						height={pageHeight ?? PREVIEW_HEIGHT}
						fit={pageHeight !== undefined}
						assets={loadAsset}
						webPictures={pictures}
					/>
				) : (
					<MarkdownDocument markdown={text} compact={true} className={styles.cardDocument} loadPicture={loadPicture} />
				)}
				{(pageHeight === undefined || pageCropped) && <span className={styles.cardFade} aria-hidden="true" />}
			</button>
		);
	}

	// A file the reply named that isn't there: its name stays in the reply as text.
	if (error === 'missing') return null;
	const tools = (
		<>
			{text !== null && (
				<button
					type="button"
					className={styles.iconButton}
					onClick={() => open('source')}
					aria-label={
						kind === 'html'
							? t({message: 'Show the code', comment: 'Document card: open the HTML file as code.'})
							: t({message: 'Show the source', comment: 'Document card: open the Markdown file as plain text.'})
					}
					title={
						kind === 'html'
							? t({message: 'Show the code', comment: 'Document card: open the HTML file as code.'})
							: t({message: 'Show the source', comment: 'Document card: open the Markdown file as plain text.'})
					}
					data-flx={`${flx}.source`}
				>
					<IconCode size={17} stroke={1.7} />
				</button>
			)}
			{text !== null && kind === 'markdown' && (
				<button
					type="button"
					className={styles.iconButton}
					onClick={() =>
						void import('@app/features/ui/commands/TextCopyCommands').then((commands) => commands.copy(i18n, text))
					}
					aria-label={t({message: 'Copy Markdown', comment: 'Document viewer: copy the Markdown text.'})}
					title={t({message: 'Copy Markdown', comment: 'Document viewer: copy the Markdown text.'})}
					data-flx={`${flx}.copy`}
				>
					<IconCopy size={17} stroke={1.7} />
				</button>
			)}
			{onDownload && (
				<button
					type="button"
					className={styles.iconButton}
					onClick={onDownload}
					aria-label={t({message: 'Download', comment: 'Document viewer: download the file.'})}
					title={t({message: 'Download', comment: 'Document viewer: download the file.'})}
					data-flx={`${flx}.download`}
				>
					<IconDownload size={17} stroke={1.7} />
				</button>
			)}
		</>
	);
	if (live) {
		const openLabel = t({message: 'Open', comment: 'Document card: open the file full size.'});
		return (
			<div
				ref={ref}
				className={clsx(styles.livePage, className)}
				style={{height: liveHeight ?? asked}}
				data-flx={flx}
				data-kind={kind}
				data-live=""
			>
				{text === null ? (
					waiting
				) : (
					<>
						<HtmlFrame
							html={text}
							title={title}
							live={true}
							onContentHeight={setLiveHeight}
							assets={loadAsset}
							webPictures={pictures}
						/>
						<div className={styles.livePageTools}>
							{tools}
							<button
								type="button"
								className={styles.iconButton}
								onClick={() => open()}
								aria-label={openLabel}
								title={openLabel}
								data-flx={`${flx}.open`}
							>
								<IconArrowsMaximize size={17} stroke={1.7} />
							</button>
						</div>
					</>
				)}
			</div>
		);
	}
	return (
		<div
			ref={ref}
			className={clsx(styles.card, className)}
			data-flx={flx}
			data-kind={kind}
			data-page={page ? '' : undefined}
		>
			<div className={styles.cardBar}>
				<span className={styles.cardIcon}>
					<DocumentKindIcon kind={kind} size={17} />
				</span>
				<span className={styles.cardTitle} title={title}>
					{title}
				</span>
				{title !== name && (
					<span className={styles.cardName} title={name}>
						{name}
					</span>
				)}
				<span className={styles.cardTools}>{tools}</span>
			</div>
			<div className={styles.cardBody}>{disabled ? null : body}</div>
			<div className={styles.cardFoot}>
				<span className={styles.cardFacts}>{facts}</span>
				{text !== null && (
					<button type="button" className={styles.openButton} onClick={() => open()} data-flx={`${flx}.open`}>
						<IconArrowsMaximize size={14} stroke={1.8} />
						{t({message: 'Open', comment: 'Document card: open the file full size.'})}
					</button>
				)}
			</div>
		</div>
	);
}
